import {
  AlertCircle,
  CheckCircle2,
  CloudOff,
  ExternalLink,
  FolderSync,
  Loader2,
  Unplug,
} from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import {
  useDisconnectClioMutation,
  useGetClioStatusQuery,
} from '@/services/api/clioService';

// Clio requires http://127.0.0.1 (not localhost) for local OAuth redirects.
// Replace "localhost" so build_absolute_uri produces a matching redirect_uri.
const BACKEND_HOST = (
  import.meta.env.VITE_APP_DJANGO_BACKEND_HOST || 'http://localhost:8000'
).replace('//localhost', '//127.0.0.1');

interface ClioIntegrationProps {
  firmId: string;
}

const ClioIntegrationSkeleton: React.FC = () => (
  <div className="border rounded-lg bg-white overflow-hidden">
    <div className="flex items-center justify-between px-5 py-4">
      <div className="flex items-center gap-3">
        <Skeleton className="h-10 w-10 rounded-lg" />
        <div className="space-y-1.5">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-3 w-48" />
        </div>
      </div>
      <Skeleton className="h-6 w-24 rounded-full" />
    </div>
  </div>
);

const ClioLogo: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 40 40"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    {/* Clio "C" logomark */}
    <circle cx="20" cy="20" r="18" fill="#1B3A5C" />
    <path
      d="M26.5 13.5C24.8 11.8 22.5 10.8 20 10.8C14.9 10.8 10.8 14.9 10.8 20C10.8 25.1 14.9 29.2 20 29.2C22.5 29.2 24.8 28.2 26.5 26.5"
      stroke="white"
      strokeWidth="3"
      strokeLinecap="round"
      fill="none"
    />
  </svg>
);

export const ClioIntegration: React.FC<ClioIntegrationProps> = ({
  firmId,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [isDisconnectDialogOpen, setIsDisconnectDialogOpen] = useState(false);

  const {
    data: status,
    isLoading,
    refetch,
  } = useGetClioStatusQuery(
    { firmId },
    { refetchOnMountOrArgChange: true },
  );

  const [disconnectClio, { isLoading: isDisconnecting }] =
    useDisconnectClioMutation();

  // Show success/error toast when redirected back from OAuth
  useEffect(() => {
    const clioParam = searchParams.get('clio');
    if (clioParam === 'connected') {
      toast.success('Clio connected successfully');
      searchParams.delete('clio');
      setSearchParams(searchParams, { replace: true });
      refetch();
    } else if (clioParam === 'error') {
      toast.error(
        'Failed to connect Clio. Please try again or contact support.',
      );
      searchParams.delete('clio');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams, refetch]);

  const handleConnect = () => {
    window.location.href = `${BACKEND_HOST}/api/integrations/clio/connect?firm_id=${firmId}`;
  };

  const handleDisconnect = async () => {
    try {
      await disconnectClio({ firmId }).unwrap();
      toast.success('Clio disconnected');
      setIsDisconnectDialogOpen(false);
    } catch {
      toast.error('Failed to disconnect Clio');
    }
  };

  if (isLoading) {
    return <ClioIntegrationSkeleton />;
  }

  const isConnected = status?.connected;

  return (
    <>
      <div className="border rounded-lg bg-white overflow-hidden">
        {/* Card header: logo, name, description, status */}
        <div className="flex items-center justify-between px-5 py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-slate-50 border border-slate-100">
              <ClioLogo className="h-6 w-6" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-semibold text-gray-900">Clio</h3>
                <span className="text-xs text-gray-500">
                  Legal practice management
                </span>
              </div>
              {isConnected ? (
                <p className="text-xs text-gray-500 mt-0.5">
                  {status.display_name || 'Clio User'}
                  {status.email ? ` \u00B7 ${status.email}` : ''}
                </p>
              ) : (
                <p className="text-xs text-gray-500 mt-0.5">
                  Sync tasks, contacts, and documents
                </p>
              )}
            </div>
          </div>

          <div className="flex items-center gap-3">
            {isConnected ? (
              <>
                <Badge
                  variant="default"
                  className="bg-emerald-50 text-emerald-700 hover:bg-emerald-50 border border-emerald-200 font-medium"
                >
                  <CheckCircle2 className="h-3 w-3 mr-1" />
                  Connected
                </Badge>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setIsDisconnectDialogOpen(true)}
                  className="text-gray-500 hover:text-red-600 h-8 px-2"
                >
                  <Unplug className="h-4 w-4" />
                </Button>
              </>
            ) : (
              <Button
                onClick={handleConnect}
                size="sm"
                className="h-8"
              >
                <ExternalLink className="h-3.5 w-3.5 mr-1.5" />
                Connect
              </Button>
            )}
          </div>
        </div>

        {/* Connected: sync status banner */}
        {isConnected && (
          <div className="border-t bg-blue-50/50 px-5 py-3">
            <div className="flex items-center gap-2 text-xs text-blue-700">
              <FolderSync className="h-3.5 w-3.5 shrink-0 text-blue-600" />
              <span className="font-medium">
                Task and document sync is active.
              </span>
              <span className="text-blue-600">
                Configure sync options in the CMS Features tab.
              </span>
            </div>
          </div>
        )}

        {/* Disconnected: empty state */}
        {!isConnected && (
          <div className="border-t bg-gray-50/50 px-5 py-3">
            <div className="flex items-center gap-2 text-xs text-gray-500">
              <CloudOff className="h-3.5 w-3.5 shrink-0" />
              <span>
                No account connected. Click Connect to authorize via Clio
                OAuth.
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Disconnect confirmation dialog */}
      <Dialog
        open={isDisconnectDialogOpen}
        onOpenChange={setIsDisconnectDialogOpen}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Disconnect Clio</DialogTitle>
            <DialogDescription>
              Are you sure you want to disconnect this Clio account? Task and
              document sync will stop, but existing synced data will remain in
              Finch.
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-start gap-3 rounded-lg bg-amber-50 p-3 text-sm text-amber-800">
            <AlertCircle className="h-5 w-5 mt-0.5 shrink-0 text-amber-600" />
            <p>
              Any in-progress sync operations will be cancelled. You can
              reconnect at any time.
            </p>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsDisconnectDialogOpen(false)}
              disabled={isDisconnecting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDisconnect}
              disabled={isDisconnecting}
            >
              {isDisconnecting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Disconnecting...
                </>
              ) : (
                'Disconnect'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
